import { isObject } from 'lodash';
import { ConfigService } from 'app/config/config.service';

type DestroyFn = () => any;

const DESTRUCTOR_ALREADY_DESTROYED_MSG = 'destructor has already been destroyed';

export interface Destroyable {
    destroy(): void;
}

function isDestroyFn(fn: unknown): fn is DestroyFn {
    return typeof fn === 'function';
}

function isDestroyableObj(obj: any): obj is Destroyable {
    return isObject(obj) && isDestroyFn((obj as any)?.destroy);
}

export class Destructor implements Destroyable {
    private _clean: Set<Destroyable | DestroyFn> = new Set();
    private _isDestroyed: boolean = false;

    constructor(...items: Array<Destroyable | DestroyFn>) {
        this.add(...items);
    }

    public add(...items: Array<Destroyable | DestroyFn>): void {
        if (this._isDestroyed) {
            // TODO: this is used to work around some issues with Cypress related to fast interactions / {force: true}
            //   I think this the more common ocurrence (and the most concerning as we're trying to do create stuff on something destroyed)
            if (ConfigService.convertDestructorDestroyErrorsToWarnings) {
                console.warn(
                    `Error(${DESTRUCTOR_ALREADY_DESTROYED_MSG}) ignored at user request (ConfigService.convertDestructorDestroyErrorsToWarnings is truthy)`,
                );
                return;
            }

            throw new Error(DESTRUCTOR_ALREADY_DESTROYED_MSG);
        }

        for (const item of items) {
            if (!item) {
                return;
            }
            if (this._clean.has(item)) {
                throw new Error('destructor already has item registered');
            }
            if (!isDestroyFn(item) && !isDestroyableObj(item)) {
                throw new Error(
                    `destructor can only be given destroy functions or destroyable objects, not: ${typeof item}`,
                );
            }
            this._clean.add(item);
        }
    }

    /**
     * Save yourself from keeping track on another boolean in your class,
     *  just use this one.
     */
    public get isDestroyed(): boolean {
        return this._isDestroyed;
    }

    // using an anonymous function property instead of a method so
    //  destructor.destroy can be passed around easily
    destroy: () => void = () => {
        if (this._isDestroyed) {
            // TODO: this is used to work around some issues with Cypress related to fast interactions / {force: true}
            //   I think this the less common ocurrence (and not as concerning as it's while we're already destroyed)
            if (ConfigService.convertDestructorDestroyErrorsToWarnings) {
                console.warn(
                    `Error(${DESTRUCTOR_ALREADY_DESTROYED_MSG}) ignored at user request (ConfigService.convertDestructorDestroyErrorsToWarnings is truthy)`,
                );
                return;
            }

            throw new Error(DESTRUCTOR_ALREADY_DESTROYED_MSG);
        }

        this._isDestroyed = true;

        this._clean.forEach((obj) => {
            if (isDestroyFn(obj)) {
                obj();
            } else {
                obj.destroy();
            }
        });

        this._clean.clear();
    };
}
